import type {DefaultOtelServiceOptions} from '@shopify/opentelemetry-sdk';

import {getConfiguration} from 'utilities/configuration';
import {isKeyShop} from 'utilities/key-shops';
import {readShopifyFeatures} from 'utilities/shopify-features';

import {MetadataMap} from './types';
import {metricIdsMapper, metricsConfig} from './config';

const resourceRegexMapper = {
  shopPromiseAsset: /\/shop_promise_pdp\.js/,
};

type AssetName = keyof typeof resourceRegexMapper;

type MeasureName = keyof typeof metricIdsMapper;

interface UniqueMetada {
  shopPromiseAssetDiscovery?: undefined;
  shopPromiseAssetQueued?: undefined;
  shopPromiseAssetFetched?: undefined;
  shopPromiseAssetExecuted?: undefined;
  resolveVariant?: undefined;
  deliveryEstimateGraphQL?: undefined;
  fcpToRender?: undefined;
  realWorldTime?: undefined;
  mountingPoint: {
    reason: 'dynamic-checkout-timeout' | 'none';
  };
  moduleFullyLoaded?: undefined;
}

interface BufferData<T extends MeasureName = any> {
  time: number;
  metadata: UniqueMetada[T];
}

const metricsBuffer = new Map<MeasureName, BufferData>();

function getMetricFromBuffer(measureName: MeasureName) {
  return metricsBuffer.get(measureName);
}

function clearMetricsBuffer() {
  metricsBuffer.clear();
}

type MetadataCondition<T extends MeasureName> =
  UniqueMetada[T] extends undefined
    ? {metadata?: undefined}
    : {metadata: UniqueMetada[T]};

type WriteBufferArg<T extends MeasureName> = {
  measureName: T;
  time: number;
} & MetadataCondition<T>;

function writeMetricToBuffer<T extends MeasureName>({
  measureName,
  time,
  metadata,
}: WriteBufferArg<T>) {
  metricsBuffer.set(measureName, {time, metadata});
}

function getMarksName(measureName: MeasureName) {
  return {
    startMark: `shop_promise_${measureName}_start`,
    endMark: `shop_promise_${measureName}_end`,
  };
}

const getStartTime = {
  firstPaint: () =>
    performance
      .getEntriesByType('paint')
      .find(({name}) => name === 'first-contentful-paint')?.startTime ?? null,
  startParsingDOM: () => {
    const [navigationEntry] = performance.getEntriesByType('navigation');

    return navigationEntry instanceof PerformanceNavigationTiming
      ? navigationEntry.responseEnd
      : null;
  },
};

type StartTimeMarkName = keyof typeof getStartTime;

async function measurePromise<T>(
  measureName: MeasureName,
  promise: Promise<T>,
) {
  const {startMark, endMark} = getMarksName(measureName);

  performance.mark(startMark);

  return promise.finally(() => {
    performance.mark(endMark);
    const perf = performance.measure(
      metricIdsMapper[measureName],
      startMark,
      endMark,
    );
    writeMetricToBuffer({
      measureName,
      time: Math.round(perf.duration),
      metadata: undefined,
    });
  });
}

interface AssetPerformance {
  duration: number;
  networkLatency: number;
  url: string;
  stalledTime: number;
  requestStart: number;
  fetched: number;
  cached: boolean;
}

function getAssetInfo(assetName: AssetName): AssetPerformance | null {
  const resource = performance
    .getEntriesByType('resource')
    .find(({name}) => resourceRegexMapper[assetName].test(name));

  if (resource instanceof PerformanceResourceTiming) {
    const {
      name,
      requestStart,
      responseStart,
      domainLookupEnd,
      domainLookupStart,
      connectEnd,
      connectStart,
      secureConnectionStart,
      responseEnd,
      duration,
      fetchStart,
      transferSize,
    } = resource;

    const dnslookup = domainLookupEnd - domainLookupStart;
    const TCPhandshake = connectEnd - connectStart;
    const secureConn =
      secureConnectionStart > 0 ? connectEnd - secureConnectionStart : 0;
    const wl =
      responseEnd - requestStart + (dnslookup + TCPhandshake + secureConn);
    const whl =
      responseStart - requestStart + (dnslookup + TCPhandshake + secureConn);
    const latency = wl - whl;

    return {
      url: name,
      duration,
      networkLatency: latency,
      fetched: responseEnd - requestStart,
      stalledTime: requestStart - fetchStart,
      requestStart: fetchStart,
      cached: transferSize === 0,
    };
  }

  return null;
}

function measureAsset(assetName: AssetName) {
  const asset = getAssetInfo(assetName);
  const startParsingDOMTime = getStartTime.startParsingDOM();

  if (!asset || asset.cached || startParsingDOMTime === null) {
    return;
  }

  const assetDiscoveryTime = Math.round(
    asset.requestStart - startParsingDOMTime,
  );
  const assetQueuedTime = Math.round(asset.stalledTime);
  const assetFetchedTime = Math.round(asset.fetched);

  writeMetricToBuffer({
    measureName: `${assetName}Discovery`,
    time: assetDiscoveryTime,
  });
  writeMetricToBuffer({
    measureName: `${assetName}Queued`,
    time: assetQueuedTime,
  });
  writeMetricToBuffer({
    measureName: `${assetName}Fetched`,
    time: assetFetchedTime,
  });
}

type MeasureOptions<T extends MeasureName> = {
  startTimeMarkName?: StartTimeMarkName;
} & MetadataCondition<T>;

function measure<T extends MeasureName>(
  measureName: T,
  options?: MeasureOptions<T>,
) {
  const {startTimeMarkName = 'firstPaint', metadata} = options || {};
  const {startMark, endMark} = getMarksName(measureName);

  const startTime = getStartTime[startTimeMarkName]();
  if (startTime === null) {
    return;
  }

  performance.mark(startMark, {startTime});
  performance.mark(endMark);

  const perf = performance.measure(
    metricIdsMapper[measureName],
    startMark,
    endMark,
  );

  writeMetricToBuffer({
    measureName,
    time: Math.round(perf.duration),
    metadata,
  } as WriteBufferArg<T>);
}

async function getDefaultOptions(): Promise<DefaultOtelServiceOptions> {
  const {releaseStage} = getConfiguration();
  const {shopId} = readShopifyFeatures();

  return {
    serviceName: 'shop-promise-pdp',
    throttleLimit: 1000 /* magic const? */,
    env: releaseStage,
    prefixMetric: false,
    metrics: await metricsConfig(),
    onRecord: (_metricName, value, labels) => [
      value,
      {
        ...labels,
        shopId: isKeyShop() ? shopId : 'non-key-shop',
      },
    ],
  };
}

async function sendMetrics(baseMetadata: MetadataMap) {
  const {DefaultOtelService} = await import('@shopify/opentelemetry-sdk');
  const otelService = new DefaultOtelService(await getDefaultOptions());

  const metrics = Array.from(metricsBuffer.entries());

  // eslint-disable-next-line no-console
  console.groupCollapsed('Shop Promise Telemetry');

  for (const [measureName, {time, metadata}] of metrics) {
    const metricId = metricIdsMapper[measureName];
    otelService.record(metricId, time, {
      ...baseMetadata,
      ...metadata,
    });
    metricsBuffer.delete(measureName);
    // eslint-disable-next-line no-console
    console.log(
      `%c Measurement "${metricId}" with time=${time}.`,
      'color: blue; background: white;',
    );
  }

  // eslint-disable-next-line no-console
  console.groupEnd();
}

export const ShopPromisePerformance = {
  measurePromise,
  measure,
  getAssetInfo,
  measureAsset,
  sendMetrics,
  getMetricFromBuffer,
  clearMetricsBuffer,
};
